/**
 * SecureEmbed Access Control - Cognito Deployment Cards
 *
 * Unified view of all Cognito user pool deployments with live pool
 * discovery, real-time user counts, health status, and drill-down
 * to per-pool user management.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Shield, ShieldCheck, Heart, GraduationCap, BookOpen, ExternalLink,
  ChevronRight, Loader2, Users, Building2, Layers, Lightbulb,
  CheckCircle, AlertCircle, Lock, Fingerprint, Globe,
  Utensils, RefreshCw, BarChart3, Info, Church,
} from 'lucide-react';
import { metricsApi, orgApi, userPoolApi } from '../services/api';

// ============================================================================
// COGNITO POOL CONFIGURATION
// ============================================================================

interface PortalLink {
  name: string;
  url: string;
}

interface CognitoPoolConfig {
  key: string;
  name: string;
  poolNamePattern: string;
  description: string;
  mfaStatus: 'enforced' | 'optional';
  groups: string[];
  portals: PortalLink[];
  federationStatus: 'native' | 'designed';
  terraformLayer: string;
  color: string;
  icon: React.ComponentType<{ className?: string }>;
}

const COGNITO_POOLS: CognitoPoolConfig[] = [
  {
    key: 'dashboard',
    name: 'Dashboard & API',
    poolNamePattern: 'secureembed-{env}-dashboard',
    description: 'Primary identity pool for platform management and API access',
    mfaStatus: 'enforced',
    groups: ['admin', 'operator', 'analyst', 'viewer'],
    portals: [
      { name: 'Management Dashboard', url: 'https://dvhpsncww7mnf.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '00-dashboard',
    color: '#7C3AED',
    icon: Shield,
  },
  {
    key: 'energy',
    name: 'Energy Portal',
    poolNamePattern: 'secureembed-{env}-energy-portal',
    description: 'Energy market analytics with tiered access control (Tier 1–3)',
    mfaStatus: 'optional',
    groups: ['Admins', 'Monitors'],
    portals: [
      { name: 'Energy Portal', url: 'https://d35p5ll6eor5e2.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '08a-analysis-portal',
    color: '#2B064F',
    icon: BarChart3,
  },
  {
    key: 'food',
    name: 'Food Interactions',
    poolNamePattern: 'secureembed-{env}-food-users',
    description: 'Food safety and interactions portal identity pool',
    mfaStatus: 'optional',
    groups: ['food-system-admin', 'food-public'],
    portals: [
      { name: 'Food Portal', url: 'https://dva4e5t7s4qq4.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '07i-food',
    color: '#059669',
    icon: Utensils,
  },
  {
    key: 'medicine',
    name: 'Medicine (MedEmbed)',
    poolNamePattern: 'secureembed-{env}-medembed-users',
    description: 'Healthcare professional identity pool for medical data access',
    mfaStatus: 'optional',
    groups: ['clinician', 'researcher', 'pharmacist', 'admin', 'viewer'],
    portals: [
      { name: 'Medicine Portal', url: 'https://d35jrhnjrc5r7x.cloudfront.net' },
    ],
    federationStatus: 'designed',
    terraformLayer: '07h-medicine',
    color: '#F43F5E',
    icon: Heart,
  },
  {
    key: 'education',
    name: 'Education (EduEmbed)',
    poolNamePattern: 'secureembed-{env}-eduembed-users',
    description: 'Education sector identity pool for curriculum and resource access',
    mfaStatus: 'optional',
    groups: ['teacher', 'administrator', 'student', 'researcher', 'viewer'],
    portals: [
      { name: 'Education Portal', url: 'https://d30tfh6ry6u3bu.cloudfront.net' },
    ],
    federationStatus: 'designed',
    terraformLayer: '07e-education',
    color: '#F97316',
    icon: GraduationCap,
  },
  {
    key: 'academic',
    name: 'Academic Discovery',
    poolNamePattern: 'secureembed-{env}-acad-discovery-users',
    description: 'Academic research identity pool for scholarly data discovery',
    mfaStatus: 'optional',
    groups: ['researcher', 'faculty', 'librarian', 'admin', 'viewer'],
    portals: [
      { name: 'Academic Portal', url: 'https://d1ajeuji49mfg5.cloudfront.net' },
    ],
    federationStatus: 'designed',
    terraformLayer: '08a-analysis-portal',
    color: '#0EA5E9',
    icon: BookOpen,
  },
  {
    key: 'innovateflow',
    name: 'InnovateFlow',
    poolNamePattern: 'secureembed-{env}-innovateflow-users',
    description: 'InnovateFlow innovation management identity pool',
    mfaStatus: 'optional',
    groups: ['innovateflow-admin', 'inventor', 'collaborator'],
    portals: [
      { name: 'InnovateFlow', url: 'https://d3h01kp97t351k.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '07k-innovateflow',
    color: '#0891B2',
    icon: Lightbulb,
  },
  {
    key: 'insurance',
    name: 'Insurance Portal',
    poolNamePattern: 'secureembed-{env}-insurance-portal',
    description: 'Insurance analytics identity pool for APRA data access',
    mfaStatus: 'optional',
    groups: ['admin', 'analyst', 'actuary', 'viewer'],
    portals: [
      { name: 'Insurance Portal', url: 'https://dqn0g8nzpikd8.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '08f-insurance-portal',
    color: '#CA8A04',
    icon: ShieldCheck,
  },
  {
    key: 'theology',
    name: 'Theology Portal',
    poolNamePattern: 'secureembed-{env}-theology-portal',
    description: 'Bible reader and scripture study identity pool',
    mfaStatus: 'optional',
    groups: ['Admins', 'Researchers'],
    portals: [
      { name: 'Theology Portal', url: 'https://dwz6w0pv4yxqa.cloudfront.net' },
    ],
    federationStatus: 'native',
    terraformLayer: '08ga-theology-portal',
    color: '#7E22CE',
    icon: Church,
  },
];

// ============================================================================
// INFO TOOLTIP CONTENT
// ============================================================================

const CARD_FIELD_INFO = [
  { label: 'Status', desc: 'Whether this Cognito user pool has been deployed to AWS. Active means the pool exists and is operational.' },
  { label: 'Users', desc: 'Estimated number of users registered in this Cognito pool. Updated in real-time from AWS.' },
  { label: 'Portals', desc: 'Web applications that authenticate against this user pool. Click to open the portal.' },
  { label: 'MFA', desc: 'Multi-Factor Authentication setting. Enforced = all users must configure TOTP. Optional = users can opt in.' },
  { label: 'Federation', desc: 'Identity federation config. Cognito Native = username/password only. External providers enable SSO.' },
  { label: 'Groups', desc: 'Cognito groups define user roles and permissions within this pool. Each group maps to an IAM role.' },
  { label: 'Layer', desc: 'The Terraform infrastructure layer that manages this pool deployment and configuration.' },
];

// ============================================================================
// HELPERS
// ============================================================================

/** Match a live pool to a config entry by name pattern. */
function matchPool(livePools: any[], config: CognitoPoolConfig): any | null {
  if (!livePools?.length) return null;
  // Convert pool name pattern to regex: {env} matches any environment name
  const regexStr = config.poolNamePattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace('\\{env\\}', '[a-z]+');
  const regex = new RegExp(`^${regexStr}$`, 'i');
  return livePools.find((p: any) => regex.test(p.name || '')) || null;
}

// ============================================================================
// COMPONENT
// ============================================================================

const AccessControl: React.FC = () => {
  const navigate = useNavigate();

  // Live pool discovery from Cognito
  const { data: poolsData, isLoading: poolsLoading, isError: poolsError, error: poolsErrorDetail, refetch: refetchPools } = useQuery({
    queryKey: ['cognito-pools'],
    queryFn: () => userPoolApi.listPools(),
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Fetch health data (dashboard pool only)
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['access-health'],
    queryFn: () => metricsApi.getHealth('dev'),
    refetchInterval: 30000,
    staleTime: 15000,
  });

  // Fetch org count
  const { data: orgData, isLoading: orgsLoading, isError: orgsError } = useQuery({
    queryKey: ['access-orgs'],
    queryFn: () => orgApi.listOrganisations(),
    staleTime: 60000,
  });

  const livePools: any[] = poolsData?.pools || [];
  const orgCount = orgData?.organisations?.length ?? 0;
  const cognitoHealth = healthData?.components?.cognito;
  const overallHealthStatus = cognitoHealth?.status || (healthLoading ? 'loading' : 'unknown');

  // Total user count across all live pools
  const totalUsers = livePools.reduce((sum: number, p: any) => sum + (p.user_count || 0), 0);
  // Count how many configured pools have a matching live pool
  const deployedCount = COGNITO_POOLS.filter(config => matchPool(livePools, config)).length;

  const getHealthLabel = (status: string) => {
    switch (status) {
      case 'healthy': return 'Healthy';
      case 'warning': return 'Warning';
      case 'critical': return 'Critical';
      case 'loading': return 'Checking...';
      default: return 'Unknown';
    }
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  const getHealthDot = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Access Control</h1>
              <p className="text-purple-200 mt-1">Cognito user pool deployments and identity management</p>
            </div>
          </div>
          <button
            onClick={() => refetchPools()}
            disabled={poolsLoading}
            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            title="Refresh pools"
          >
            <RefreshCw className={`w-5 h-5 ${poolsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {poolsLoading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : poolsError ? <span className="text-red-500">Error</span> : `${deployedCount}/${COGNITO_POOLS.length}`}
              </p>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                Pools Deployed
                <span className="relative group">
                  <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-iflow-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 text-center leading-relaxed">
                    Configured Cognito user pools that have been deployed to AWS. {poolsError ? 'Live pool status is currently unavailable.' : `${deployedCount} of ${COGNITO_POOLS.length} configured pools are live.`}
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              {poolsLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : poolsError ? (
                <p className="text-2xl font-bold text-red-500">Error</p>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                Total Users
                <span className="relative group">
                  <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-iflow-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 text-center leading-relaxed">
                    Sum of all users across all deployed Cognito user pools. Uses AWS EstimatedNumberOfUsers.
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              {orgsLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : orgsError ? (
                <p className="text-2xl font-bold text-red-500">Error</p>
              ) : (
                <p className="text-2xl font-bold text-gray-900">{orgCount === 0 ? 'None' : orgCount}</p>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                Organizations
                <span className="relative group">
                  <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-iflow-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 text-center leading-relaxed">
                    Number of multi-tenant organizations registered in the platform.
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              {healthLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <p className={`text-lg font-bold ${getHealthColor(overallHealthStatus)}`}>
                  {getHealthLabel(overallHealthStatus)}
                </p>
              )}
              <p className="text-xs text-gray-500 flex items-center gap-1">
                Cognito Health
                <span className="relative group">
                  <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-iflow-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 text-center leading-relaxed">
                    Overall health status of the primary Cognito user pool. Checks pool existence and responsiveness.
                  </span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {poolsError && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
          <span className="text-red-700 flex-1">{(poolsErrorDetail as Error)?.message || 'Failed to load Cognito pools. Data shown may be incomplete.'}</span>
          <button
            onClick={() => refetchPools()}
            className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs font-medium"
          >
            Retry
          </button>
        </div>
      )}

      {/* Card Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {COGNITO_POOLS.map((pool) => {
          const Icon = pool.icon;
          const isDashboard = pool.key === 'dashboard';
          const livePool = matchPool(livePools, pool);
          const isDeployed = !!livePool;
          const poolUserCount = livePool?.user_count ?? 0;
          const liveGroups: string[] = livePool?.groups || [];
          const poolHealthStatus = isDashboard ? overallHealthStatus : (isDeployed ? 'healthy' : 'configured');

          return (
            <div
              key={pool.key}
              onClick={() => navigate(`/access/${pool.key}`)}
              className="bg-gray-100 rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md hover:border-gray-200 transition-all"
            >
              {/* Card Header */}
              <div
                className="p-4 flex items-center gap-3 border-b border-gray-100"
                style={{ borderLeftWidth: 4, borderLeftColor: pool.color }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: pool.color + '18' }}
                >
                  <span style={{ color: pool.color }}><Icon className="w-5 h-5" /></span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{pool.name}</h3>
                  <p className="text-xs text-gray-500 truncate">{livePool?.name || pool.poolNamePattern}</p>
                </div>
                <span className="relative group/info" onClick={(e) => e.stopPropagation()}>
                  <Info className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                  <span className="absolute top-full right-0 mt-2 w-72 px-4 py-3 bg-iflow-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover/info:opacity-100 group-hover/info:pointer-events-auto transition-opacity z-50 leading-relaxed space-y-1.5">
                    {CARD_FIELD_INFO.map((f) => (
                      <span key={f.label} className="block"><span className="font-semibold">{f.label}:</span> {f.desc}</span>
                    ))}
                    {isDashboard && (
                      <span className="block"><span className="font-semibold">Organizations:</span> Multi-tenant organizations registered in the platform. Each org can have multiple users.</span>
                    )}
                  </span>
                </span>
                {isDeployed ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-400 rounded-full text-xs font-medium">
                    Not Deployed
                  </span>
                )}
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Health + User Count */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1.5">
                    {isDeployed ? (
                      <CheckCircle className="w-3.5 h-3.5" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5" />
                    )}
                    Status
                  </span>
                  <span className="flex items-center gap-1.5">
                    {isDashboard && healthLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                    ) : (
                      <span className={`w-2 h-2 rounded-full ${isDeployed ? getHealthDot(poolHealthStatus) : 'bg-gray-300'}`} />
                    )}
                    <span className={isDeployed ? getHealthColor(poolHealthStatus) : 'text-gray-500'}>
                      {isDeployed ? (isDashboard ? getHealthLabel(poolHealthStatus) : 'Healthy') : 'Configured'}
                    </span>
                  </span>
                </div>

                {/* User count (live) */}
                {isDeployed && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Users
                    </span>
                    <span className="font-semibold text-gray-900">{poolUserCount}</span>
                  </div>
                )}

                {/* Portals */}
                <div className="text-sm">
                  <span className="text-gray-500 flex items-center gap-1.5 mb-1">
                    <Globe className="w-3.5 h-3.5" />
                    Portals
                  </span>
                  <div className="ml-5 space-y-1">
                    {pool.portals.map((portal) => (
                      portal.url ? (
                        <a
                          key={portal.name}
                          href={portal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 text-purple-600 hover:text-purple-800 transition-colors"
                        >
                          {portal.name}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span key={portal.name} className="text-gray-400">{portal.name} (not deployed)</span>
                      )
                    ))}
                  </div>
                </div>

                {/* MFA */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    MFA
                  </span>
                  <span className={
                    (isDeployed && livePool?.mfa_config)
                      ? (livePool.mfa_config === 'ON' ? 'text-green-700 font-medium' : 'text-gray-600')
                      : (pool.mfaStatus === 'enforced' ? 'text-green-700 font-medium' : 'text-gray-600')
                  }>
                    {(isDeployed && livePool?.mfa_config)
                      ? (livePool.mfa_config === 'ON' ? 'Enforced (TOTP)' : livePool.mfa_config === 'OPTIONAL' ? 'Optional (TOTP)' : 'Disabled')
                      : (pool.mfaStatus === 'enforced' ? 'Enforced (TOTP)' : 'Optional (TOTP)')}
                  </span>
                </div>

                {/* Federation */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1.5">
                    <Fingerprint className="w-3.5 h-3.5" />
                    Federation
                  </span>
                  <span className="text-gray-600">
                    {(isDeployed && livePool?.identity_providers)
                      ? (livePool.identity_providers.length > 0
                          ? livePool.identity_providers.map((p: any) => p.name).join(', ')
                          : 'Cognito Native')
                      : (pool.federationStatus === 'native' ? 'Cognito Native' : 'Designed')}
                  </span>
                </div>

                {/* Drill-down rows — Dashboard pool only */}
                {isDashboard && (
                  <>
                    <div
                      onClick={(e) => { e.stopPropagation(); navigate('/access/organizations'); }}
                      className="flex items-center justify-between hover:bg-gray-50 cursor-pointer p-2 -mx-2 rounded-lg transition-colors"
                    >
                      <span className="text-sm flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-700">Organizations</span>
                        {orgsLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                        ) : (
                          <span className="text-gray-400 font-medium">({orgCount})</span>
                        )}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                    <div
                      onClick={(e) => { e.stopPropagation(); navigate('/access/dashboard'); }}
                      className="flex items-center justify-between hover:bg-gray-50 cursor-pointer p-2 -mx-2 rounded-lg transition-colors"
                    >
                      <span className="text-sm flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-700">Manage Users</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </>
                )}

                {/* Groups */}
                <div className="text-sm">
                  <span className="text-gray-500 flex items-center gap-1.5 mb-1.5">
                    <Users className="w-3.5 h-3.5" />
                    Groups ({(isDeployed && liveGroups.length > 0 ? liveGroups : pool.groups).length} roles)
                  </span>
                  <div className={`ml-5 flex flex-wrap gap-1.5${!isDeployed ? ' opacity-50' : ''}`}>
                    {(isDeployed && liveGroups.length > 0 ? liveGroups : pool.groups).map((group) => (
                      <span
                        key={group}
                        className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                      >
                        {group}
                      </span>
                    ))}
                  </div>
                  {!isDeployed && (
                    <span className="ml-5 text-xs text-gray-400 italic">configured — pool not deployed</span>
                  )}
                </div>

                {/* Terraform Layer */}
                <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-50">
                  <span className="text-gray-500 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" />
                    Layer
                  </span>
                  <span className="text-gray-500 font-mono text-xs">{pool.terraformLayer}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccessControl;
